import {
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Divider,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { findAllAnalysisRuns } from "../monitorsApi";
import { IconPrinter, IconReport, IconSettings } from "@tabler/icons-react";
import AnalysisResultCard from "./AnalysisResultCard";

export default function AllAnalysisResults({ brandId }) {
  const [analysisRuns, setAnalysisRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [, setLocation] = useLocation();

  const reloadAnalysisRuns = async () => {
    setIsLoading(true);
    try {
      const runs = await findAllAnalysisRuns(brandId);
      setAnalysisRuns(runs);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    reloadAnalysisRuns();
  }, [brandId]);

  if (isLoading) {
    return (
      <Stack align="center" py="xl" mt="md">
        <Loader size="md" />
      </Stack>
    );
  }

  if (analysisRuns.length === 0) {
    return (
      <Stack gap="md" align="center" py="xl" mt="md">
        <ThemeIcon size={60} radius="xl" variant="light" color="blue">
          <IconReport size={30} />
        </ThemeIcon>
        <Title order={3}>No Reports Yet</Title>
        <Text c="dimmed" ta="center" maw={500}>
          Reports are generated automatically when your monitors run.
          Set up monitors to start collecting data and generating reports.
        </Text>
        <Button
          variant="light"
          leftSection={<IconSettings size={16} />}
          onClick={() => setLocation("/llm-config?tab=monitors")}
        >
          Set Up Monitors
        </Button>
      </Stack>
    );
  }

  return (
    <Stack mt="md">
      {analysisRuns.map((run) => (
        <Paper withBorder key={run.id} shadow="xs" p="md">
          <Group justify="space-between" align="center">
            <Stack>
              <Group>
                <Badge>{run.status}</Badge>
                <Text fz="lg" fw={700}>
                  {run.totalQueriesFinished} Queries
                </Text>
                <Text fz="sm" c="dimmed">
                  {new Date(run.startTimeUtc).toLocaleString()} &mdash;{" "}
                  {new Date(run.endTimeUtc).toLocaleString()}
                </Text>
              </Group>
            </Stack>

            <Button
              variant="outline"
              size="sm"
              leftSection={<IconPrinter />}
              component="a"
              target="_blank"
              href={`/reports/${run.id}/print?brandId=${brandId}`}
            >
              Print Report
            </Button>
          </Group>{" "}
          <Divider my="sm" />
          {run.analyses
            ?.filter((p) => p.status != "superseded")
            .sort(
              (a, b) => b.providerIds?.length ?? 0 - a.providerIds?.length ?? 0
            )
            .map((analysis) => (
              <AnalysisResultCard
                key={analysis.id}
                analysis={analysis}
                brandId={brandId}
                run={run}
                reloadAnalysisRuns={reloadAnalysisRuns}
              />
            ))}
        </Paper>
      ))}
    </Stack>
  );
}
